import { Box, Circle, HStack, Tag, Text, VStack } from '@chakra-ui/react'
import type React from 'react'

export enum StepNavigatorItemStatus {
  Valid = 'valid',
  Invalid = 'invalid',
  NotConfigured = 'not-configured',
  NotConfiguredOptional = 'not-configured-optional',
}

export interface StepNavigatorItemProps {
  label: string
  status: StepNavigatorItemStatus
  onClick: () => void
  children?: StepNavigatorItemProps[] // For nested items
  disabled?: boolean
}

// SVG dash pattern for optional circles
const DashedCircle = ({
  color,
  size = '16px',
}: { color: string; size?: string }) => (
  <Box position="relative" width={size} height={size} mr={2} flexShrink={0}>
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: 'absolute', top: 0, left: 0 }}
      aria-labelledby="Status indicator"
      role="img"
    >
      <title id="optionalItemTitleId">Step status indicator title</title>
      <circle
        cx="8"
        cy="8"
        r="7"
        fill={color}
        stroke="var(--chakra-colors-gray-400)"
        strokeWidth="2"
        strokeDasharray="4 2"
      />
    </svg>
  </Box>
)

export const StepNavigatorItem: React.FC<StepNavigatorItemProps> = ({
  label,
  status,
  onClick,
  children,
  disabled = false,
}) => {
  const color = {
    [StepNavigatorItemStatus.Valid]: 'green.400',
    [StepNavigatorItemStatus.Invalid]: 'red.400',
    [StepNavigatorItemStatus.NotConfigured]: 'gray.300',
    [StepNavigatorItemStatus.NotConfiguredOptional]: 'gray.300',
  }[status]

  const isOptional = status === StepNavigatorItemStatus.NotConfiguredOptional

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        mb={2}
        onClick={disabled ? undefined : onClick}
        _hover={disabled ? {} : { opacity: 0.8 }}
        opacity={disabled ? 0.5 : 1}
        pointerEvents={disabled ? 'none' : 'auto'}
      >
        {isOptional ? (
          <DashedCircle
            color={`var(--chakra-colors-${color.replace('.', '-')})`}
          />
        ) : (
          <Circle
            size="16px"
            bg={color}
            mr={2}
            border="2px solid"
            borderColor="gray.400"
          />
        )}
        <HStack gap={1}>
          <Text fontSize="sm">{label}</Text>
          {isOptional && (
            <Tag.Root
              size="sm"
              variant="subtle"
              colorPalette="gray"
              fontSize="xs"
            >
              <Tag.Label>Optional</Tag.Label>
            </Tag.Root>
          )}
        </HStack>
      </Box>

      {children && children.length > 0 && (
        <VStack ml={6} gap={0} alignItems="flex-start">
          {children.map((childItem) => (
            <StepNavigatorItem key={childItem.label} {...childItem} />
          ))}
        </VStack>
      )}
    </Box>
  )
}
